import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Cache } from '../cache/index';
import { PI } from '../constants-types';
import {
    Impression,
    ReportType,
    ReportTypeType,
    SurveyUserTypes
} from '../constants-types/Enums';
import {
    BehavioralReportConfigEmailDetails,
    BehavioralReportConfiguration,
    OnboardingBehavioralReportConfiguration,
    ReportRetrievalType
} from '../core-data-model/BehavioralReportConfiguration.model';
import { BehavioralReportData } from '../core-data-model/BehavioralReportData.model';
import { CognitiveReportData } from '../core-data-model/CognitiveReportData.model';
import { EmailSharingCommand } from '../core-data-model/EmailList.model';
import { FilterInfo, FilterType } from '../core-data-model/FilterInfo.model';
import { JobMatchingPerson } from '../core-data-model/Job.model';
import { OnboardingKitIncludedReports } from '../core-data-model/OnboardingKitIncludedReports.model';
import {
    Person,
    PersonUpdate,
    PersonWithDetails
} from '../core-data-model/Person.model';
import {
    BAReportServer,
    PersonDetailsServer,
    PersonServerPost,
    PersonServerResponse
} from '../core-data-model/PersonApi.serverinterface';
import { PersonAssessmentConsent } from '../core-data-model/PersonAssessmentConsent.model';
import { ReferencePatternInformationServer } from '../core-data-model/ReferencePatternInformation.interface';
import { ReportEmailConfiguration } from '../core-data-model/ReportEmailConfiguration.model';
import {
    PeopleAndJobsSearchResult,
    PeopleAndJobsServerEntry
} from '../core-data-model/SearchApi.serverinterface';
import { SearchState } from '../core-data-model/SearchState.model';
import { AsyncFolderDecorator } from '../core-services/Folders.dataservice';
import { ObjectCacherService } from '../core-services/ObjectCacher.service';
import { PeopleAndJobsQueryGeneratorService } from '../core-services/PeopleAndJobsQueryGenerator.service';
// Circular dependency
import { PersonApiTransformer } from '../core-services/PersonApi.datatransform';
import { PortablePersonPageImageTransform } from '../core-services/PortablePersonPage.imagetransform';
import { SearchTransformers } from '../core-services/SearchApi.datatransform';
import { SearchStateFactory } from '../core-services/SearchStateFactory';
import { HttpHeaderContentType } from '../shared/Enums';
import { ApiService } from '../system-services/Api.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { PdfReference } from '../system-services/PdfReference';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { ReportUtilities } from '../text-utilities/Report.utility';
import { TranslationService } from '../translations';

interface PersonApiUpdateBase {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    gender?: string;
    email?: string;
    folderPath?: string;
    isArchived?: boolean;
    associatedPositionTitle?: string;
    externalPersonId?: string;
    impression?: Impression;
    // Cognitive time limit
    defaultAssessmentTimeLimitInMinutes?: number;
    associatedJobId?: string;
    removeJobAssociation?: boolean;
    userId?: string;
    displayColor?: string;
}

// Input data type for update method
// This separation is done so that we can do convenient mappings from client
// properties to what needs to get sent to the server in one place
export interface PersonApiUpdate extends PersonApiUpdateBase {
    type?: SurveyUserTypes;
}
// Filtering by Person GUIDs in getPersonDetails() breaks when the
// data query contains more than 18 Persons. Set to 15 to be safe.
export const oDataQueryFilterMax = 15;

@Injectable({
    providedIn: 'root'
})
export class PersonApi {
    private personDetailsCache = new Cache<Person, string>(
        'personDetailsCache',
        (personId: string) => personId
    );
    // private mdcUrlCache = new Cache<SafeResourceUrl, string>(
    //     'mdcUrlCache',
    //     (personId: string) => personId
    // );

    constructor(
        private api: ApiService,
        private userProfile: UserProfileSettingsService,
        private transformer: PersonApiTransformer,
        private searchTransformers: SearchTransformers,
        private peopleAndJobsQueryGeneratorService: PeopleAndJobsQueryGeneratorService,
        private searchStateFactory: SearchStateFactory,
        private TR: TranslationService,
        private objectCache: ObjectCacherService,
        private imageTransformer: PortablePersonPageImageTransform,
        private initData: InitDataService,
        private reportUtil: ReportUtilities,
        private httpClient: HttpClient
    ) {}

    /**
     * Helper to encapsulate the person details request and return a properly
     * typed promise denoting the returned data.
     *
     * @param personId
    @cacheable(
        function (this: PersonApi) { return this.personDetailsCache; },
        (personId: string) => personId,
        (personId: string, clearCache: boolean) => clearCache,
        null,
        oneDaysMilli
    )
    private getPersonDetailsFromServer(
        personId: string,
        clearCache: boolean,
        companyId: string = this.userProfile.currentCompany,
    ) {
        return this.api.get<PersonDetailsServer>(`/api/v2/companies/${companyId}/persons/${personId}/details`);
    }*/

    async getPersonDefaultCognitiveTime(
        personId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<number> {
        const [time] = await this.api.get<number>(
            `/api/v1/companies/${companyId}/persons/${personId}/cognitiveAssessmentsDefaultTimeLimit`
        );
        return time;
    }

    sendReportEmail(
        peopleToReportOn: string[] | { personId: string; gender: string }[],
        reportType: ReportTypeType,
        reportEmailConfig: ReportEmailConfiguration,
        companyId: string = this.userProfile.currentCompany,
        reportConfig?: BehavioralReportConfiguration,
        language: string = this.TR.currentLocale
    ) {
        const payload = this.transformer.transformToServerReportConfig(
            reportConfig,
            reportType,
            peopleToReportOn,
            reportEmailConfig,
            language
        );

        const endpointVersion = reportType.endpointVersion
            ? reportType.endpointVersion
            : 'v1';
        return this.api.post(
            `/api/${endpointVersion}/companies/${companyId}/persons/${reportType.endpoint}`,
            payload
        );
    }

    // This is intended to replace updatePerson
    patch(update: PersonUpdate) {
        const companyId = this.userProfile.currentCompany;
        const personId = update.originalObject.id;
        const body = this.transformer.transformPatch(update);
        return this.api
            .patch(`/api/v3/companies/${companyId}/persons/${personId}`, body)
            .then((response) => {
                // Make sure to clear the cache after we've updated the resource
                this.personDetailsCache.clear(personId);
                return response;
            });
    }

    updatePerson(
        personId: string,
        update: PersonApiUpdate,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api
            .patch(`/api/v3/companies/${companyId}/persons/${personId}`, update)
            .then((response) => {
                // Make sure to clear the cache after we've updated the resource
                this.personDetailsCache.clear(personId);
                return response;
            });
    }

    setAssociation(
        personId: string,
        userId: string,
        companyId: string = this.userProfile.currentCompany
    ) {
        const baseApi = PI?.appSettings?.baManagementURL;
        this.httpClient.patch(
            `${baseApi}/api/v5/companies/${companyId}/${personId}/associateUser/${userId}`,
            null
        );
        // Make sure to clear the cache after we've updated the resource
        this.personDetailsCache.clear(personId);
        return;
    }

    unsetAssociation(
        personId: string,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api.delete(
            `/api/v4/companies/${companyId}/persons/${personId}/associateduser`
        );
    }

    @AsyncFolderDecorator.waitForFolders()
    getPersonDetails(
        personIds: string[],
        bypassCache = false
    ): Promise<Person[]> {
        // This code was done quickly to handle a majority of cases and should
        // be rethought
        if (personIds.length === 1) {
            const cachedResponse = this.objectCache.get(personIds[0]);
            if (cachedResponse && !bypassCache) {
                return Promise.resolve([cachedResponse]);
            }
        }

        const personIdsLists: string[][] = [];
        for (let i = 0; i < personIds.length; i += oDataQueryFilterMax) {
            personIdsLists.push(personIds.slice(i, i + oDataQueryFilterMax));
        }

        return Promise.all(
            personIdsLists.map((filter) => {
                let singlePersonId: string;
                let oDataQueryState = this.searchStateFactory.newSearchState();
                oDataQueryState = filter.reduce((searchState, personId) => {
                    singlePersonId = personId;
                    return searchState.addFilter(
                        new FilterInfo({
                            type: FilterType.FindPersonById,
                            parameters: {
                                personId
                            }
                        })
                    );
                }, oDataQueryState);
                // Note: Overwrite the default of max size of 10 as this is being used out side of the context of the table
                // Ideally we should use this for returning less than 10 people
                // This is a time being fix as it breaks the Monitor Survey Progress in Strategy Assessment
                oDataQueryState = oDataQueryState.setPageSize(50);
                const oDataQuery =
                    this.peopleAndJobsQueryGeneratorService.createODataString(
                        oDataQueryState
                    );
                return this.api
                    .get<PeopleAndJobsSearchResult>(
                        `/api/v2/companies/${this.userProfile.currentCompany}/peopleandjobs?${oDataQuery}`
                    )
                    .then(([results]) => {
                        let people: Person[];
                        // There is an issue with the the above odata query that sometimes the indexer has not completed by the time the user comes here
                        // to view the person page for a recently completed BA.  As such we are checking to see if the reference pattern data exists and if
                        // no then go back to the server and fetch it with a different lookup that doesnt rely on the index data to be present.
                        if (
                            results.peopleAndJobsSearchResult.some(
                                (x) =>
                                    x.assessmentUser.assessmentUserId ==
                                        singlePersonId &&
                                    !x.referencePatternInformation
                            )
                        ) {
                            return this.getPersonReferencePatternInformation(
                                singlePersonId
                            ).then((referencePattern) => {
                                results.peopleAndJobsSearchResult.find(
                                    (x) =>
                                        x.assessmentUser.assessmentUserId ==
                                        singlePersonId
                                ).referencePatternInformation =
                                    referencePattern;

                                people = this.transformRequests(
                                    results.peopleAndJobsSearchResult
                                );
                                people.forEach((person) => {
                                    this.objectCache.put(person.id, person);
                                });

                                return people;
                            });
                        } else {
                            people = this.transformRequests(
                                results.peopleAndJobsSearchResult
                            );
                            people.forEach((person) => {
                                this.objectCache.put(person.id, person);
                            });
                        }

                        return people;
                    });
            })
        ).then((setOfResults) =>
            setOfResults.reduce(
                (existing, current) => existing.concat(current),
                []
            )
        );
    }

    getPersonReferencePatternInformation(
        personId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<ReferencePatternInformationServer> {
        return this.api
            .get<PersonDetailsServer>(
                `/api/v2/companies/${companyId}/persons/${personId}/details`
            )
            .then(([result]) => result.referencePatternInformation);
    }

    getPersonDetailsByEmails(emailAddresses: string[]): Promise<Person[]> {
        if (emailAddresses.length === 1) {
            const cachedResponse = this.objectCache.get(emailAddresses[0]);
            if (cachedResponse) {
                return Promise.resolve([cachedResponse]);
            }
        }

        let oDataQueryState = this.searchStateFactory.newSearchState();
        oDataQueryState = emailAddresses.reduce(
            (searchState, emailAddress) =>
                searchState.addFilter(
                    new FilterInfo({
                        type: FilterType.FindPersonByEmail,
                        parameters: {
                            emailAddress
                        }
                    })
                ),
            oDataQueryState
        );
        const oDataQuery =
            this.peopleAndJobsQueryGeneratorService.createODataString(
                oDataQueryState
            );
        return this.api
            .get<PeopleAndJobsSearchResult>(
                `/api/v2/companies/${this.userProfile.currentCompany}/peopleandjobs?${oDataQuery}`
            )
            .then(([results]) => {
                const people: Person[] = this.transformRequests(
                    results.peopleAndJobsSearchResult
                );
                people.forEach((person) => {
                    this.objectCache.put(person.id, person);
                });
                return people;
            });
    }

    private transformRequests(results: PeopleAndJobsServerEntry[]): Person[] {
        if (results === null) {
            return null;
        }
        return results
            .map((r) => this.searchTransformers.transformPeopleAndJobs(r))
            .filter((r): r is Person => r instanceof Person);
    }

    purge(personId: string) {
        return this.api.post<any, any>(
            `/api/v3/companies/${this.userProfile.currentCompany}/persons/${personId}/anonymize`,
            null
        );
    }

    getPersonAssessmentConsentDetails(
        personId: string
    ): Promise<PersonAssessmentConsent[]> {
        return this.api
            .get<any>(
                `/api/v3/companies/${this.userProfile.currentCompany}/persons/${personId}/personConsentDetails`
            )
            .then(([response]) => {
                if (response) {
                    return response.map((assessment: any) =>
                        this.transformer.transformToPersonAssessmentConsent(
                            assessment
                        )
                    );
                }
                return [];
            });
    }

    getOnboardingReports(
        personId: string,
        actionEndpoint: ReportRetrievalType = 'Print',
        includedReports: OnboardingKitIncludedReports,
        companyId: string = this.userProfile.currentCompany,
        reportConfig?: OnboardingBehavioralReportConfiguration
    ): Promise<SafeResourceUrl> {
        const payload = {
            personId,
            ...includedReports,
            ...reportConfig
        };

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}OnBoardingKit`,
                payload,
                postResourceType
            );
        } else {
            return this.api.postBloblessResource(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}OnBoardingKit`,
                payload
            );
        }
    }

    getBehavioralReports(
        personsToReport: { personId: string; gender: string }[],
        actionEndpoint: ReportRetrievalType = 'Print',
        reportConfig: BehavioralReportConfiguration,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BlobPart> {
        const payload = {
            personsToReportOn: personsToReport,
            reportLanguage: reportConfig.languageLocale,
            isManagementStyle: reportConfig.isManagement,
            isSellingStyle: reportConfig.isSelling,
            isInfluencingStyle: reportConfig.isInfluencing
        };

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint === 'Email') {
            return this.api.postBloblessResource(
                `/api/v1/companies/${companyId}/persons/EmailBAReports`,
                payload
            );
        } else if (actionEndpoint === 'Print') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/PrintBehavioralReports`,
                payload,
                postResourceType
            );
        } else if (actionEndpoint === 'Download') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/DownloadBehavioralReports`,
                payload,
                postResourceType
            );
        }
    }

    getPlacardPdf(
        personIds: string[],
        actionEndpoint: ReportRetrievalType = 'Print',
        emailDetails: BehavioralReportConfigEmailDetails = {},
        language: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<SafeResourceUrl | any> {
        const payload = {
            personsToReportOn: personIds,
            reportLanguage: language,
            ...emailDetails
        };
        if (actionEndpoint === 'Email') {
            // This is a case different from our typical pattern and
            //   the options were to do a refactor of the backend or to simply
            //   pass along this flag.
            (payload as any).emailReportToAssessmentTaker = true;
        }

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}Placards`,
                payload,
                postResourceType
            );
        } else {
            return this.api.postBloblessResource(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}Placards`,
                payload
            );
        }
    }

    getPdcPdf(
        personIds: string[],
        actionEndpoint: ReportRetrievalType = 'Print',
        emailDetails: BehavioralReportConfigEmailDetails = {},
        language: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<SafeResourceUrl | any> {
        const payload = {
            personsToReportOn: personIds,
            reportLanguage: language,
            ...emailDetails
        };
        if (actionEndpoint === 'Email') {
            // This is a case different from our typical pattern and
            //   the options were to do a refactor of the backend or to simply
            //   pass along this flag.
            (payload as any).emailReportToAssessmentTaker = true;
        }

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}PDCs`,
                payload,
                postResourceType
            );
        } else {
            return this.api.postBloblessResource(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}PDCs`,
                payload
            );
        }
    }

    getInterviewGuide(
        personId: string,
        jobId: string,
        actionEndpoint: ReportRetrievalType = 'Print',
        emailDetails: BehavioralReportConfigEmailDetails = {},
        language: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BlobPart> {
        const payload = {
            personId,
            jobId,
            guideLanguage: language,
            ...emailDetails
        };

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v3/companies/${companyId}/persons/${actionEndpoint}InterviewGuide`,
                payload,
                postResourceType
            );
        } else {
            return this.api.postBloblessResource(
                `/api/v3/companies/${companyId}/persons/${actionEndpoint}InterviewGuide`,
                payload
            );
        }
    }

    getMdcPdf(
        personIds: string[],
        actionEndpoint: ReportRetrievalType = 'Print',
        emailDetails: BehavioralReportConfigEmailDetails = {},
        language: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<SafeResourceUrl | any> {
        const payload = {
            personsToReportOn: personIds,
            reportLanguage: language,
            ...emailDetails
        };
        if (actionEndpoint === 'Email') {
            // This is a case different from our typical pattern and
            //   the options were to do a refactor of the backend or to simply
            //   pass along this flag.
            (payload as any).emailReportToAssessmentTaker = true;
        }

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}MDCs`,
                payload,
                postResourceType
            );
        } else {
            return this.api.postBloblessResource(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}MDCs`,
                payload
            );
        }
    }

    getMsgPdf(
        personIds: string[],
        actionEndpoint: ReportRetrievalType = 'Print',
        emailDetails: BehavioralReportConfigEmailDetails = {},
        language: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<SafeResourceUrl | any> {
        const payload = {
            personsToReportOn: personIds,
            reportLanguage: language,
            ...emailDetails
        };
        if (actionEndpoint === 'Email') {
            // This is a case different from our typical pattern and
            //   the options were to do a refactor of the backend or to simply
            //   pass along this flag.
            (payload as any).emailReportToAssessmentTaker = true;
        }

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v1/companies/${companyId}/persons/${actionEndpoint}MSGs`,
                payload,
                postResourceType
            );
        }

        return this.api.postBloblessResource(
            `/api/v1/companies/${companyId}/persons/${actionEndpoint}MSGs`,
            payload
        );
    }

    getJobsThatMatchPerson(
        personId: string,
        searchState: SearchState,
        companyId: string = this.userProfile.currentCompany
    ): Promise<JobMatchingPerson[]> {
        return this.api
            .get(
                `/api/v3/companies/${companyId}/persons/${personId}/bestMatchJobs?$top=${searchState.pageState.resultsPerPage}`
            )
            .then(([response]) =>
                this.transformer.transformJobMatchesToJobs(response)
            );
    }

    private getReportEmail(emailSettings: EmailSharingCommand, payload: any) {
        if (emailSettings) {
            payload.emailDetails = {
                emailTo: emailSettings.emails.join(','),
                emailSubject: emailSettings.subject,
                emailMessageBody: emailSettings.message
            };
        }

        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/generateReport`,
            HttpHeaderContentType.PDF,
            true,
            payload
        );
    }

    private emailPersonDetailsReport(
        personDetails: PersonWithDetails,
        behavioralShowJobMatch: boolean,
        emailSettings: EmailSharingCommand,
        languageLocale: string = this.TR.currentLocale
    ) {
        const payload: any = {
            url: `imageservice/persondetails?languageLocale=${languageLocale}`,
            body: this.transformer.prepareReport(
                personDetails,
                personDetails.patternInsights,
                behavioralShowJobMatch
            ),
            fileName: this.reportUtil.getPersonReportName(
                ReportType.BehavioralOverview,
                personDetails
            )
        };

        return this.getReportEmail(emailSettings, payload);
    }

    getPersonDetailsReport(
        personDetails: PersonWithDetails,
        behavioralShowJobMatch: boolean,
        emailSettings: EmailSharingCommand,
        languageLocale = this.TR.currentLocale
    ) {
        const url = encodeURIComponent(
            `imageservice/persondetails?languageLocale=${languageLocale}`
        );
        if (emailSettings) {
            return this.emailPersonDetailsReport(
                personDetails,
                behavioralShowJobMatch,
                emailSettings,
                languageLocale
            );
        }
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/reportImage?url=${url}&fileName=PersonDetails`,
            HttpHeaderContentType.PDF,
            true,
            this.transformer.prepareReport(
                personDetails,
                personDetails.patternInsights,
                behavioralShowJobMatch
            )
        );
    }

    getPortablePersonPage(
        personDetails: PersonWithDetails,
        emailSettings: EmailSharingCommand,
        languageLocale = this.TR.currentLocale
    ) {
        const url = encodeURIComponent(
            `imageservice/portablepersonpage?languageLocale=${languageLocale}`
        );
        if (emailSettings) {
            return this.emailPersonSnapshot(
                personDetails,
                emailSettings,
                languageLocale
            );
        }
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/reportImage?url=${url}&fileName=PortablePersonDetails`,
            HttpHeaderContentType.PDF,
            true,
            this.imageTransformer.transformFromSpaToServer(
                personDetails,
                personDetails.patternInsights,
                this.initData.user.email
            )
        );
    }

    emailPersonSnapshot(
        personDetails: PersonWithDetails,
        emailSettings: EmailSharingCommand,
        languageLocale = this.TR.currentLocale
    ) {
        const payload: any = {
            url: `imageservice/portablepersonpage?languageLocale=${languageLocale}`,
            body: this.imageTransformer.transformFromSpaToServer(
                personDetails,
                personDetails.patternInsights,
                this.initData.user.email
            ),
            emailDetails: {
                emailTo: emailSettings.emails.join(','),
                emailSubject: emailSettings.subject,
                emailMessageBody: emailSettings.message
            },
            fileName: this.reportUtil.getPersonReportName(
                ReportType.PortablePersonPage,
                personDetails
            )
        };

        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/generateReport`,
            HttpHeaderContentType.PDF,
            true,
            payload
        );
    }

    getCogReportInfo(
        personId: string,
        showSensitiveInfo = true,
        companyId: string = this.userProfile.currentCompany
    ): Promise<CognitiveReportData> {
        return this.getCogReportInfoData(
            personId,
            companyId,
            showSensitiveInfo
        ).then(([response]) => {
            const cogReportInfo =
                this.transformer.transformToCogReport(response);
            return cogReportInfo;
        });
    }

    private getCogReportInfoData(
        personId: string,
        companyId: string,
        showSensitiveData = true,
        languageLocale: string = this.TR.currentLocale
    ) {
        return this.api.get<CognitiveReportData>(
            `/api/v3/companies/${companyId}/persons/${personId}/cogReportInfo?languageLocale=${languageLocale}&showSensitiveInfo=${showSensitiveData}`
        );
    }

    getCogReportBulk(
        personIds: string[],
        showSensitiveData = true,
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PdfReference> {
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/companies/${companyId}/persons/PrintCognitiveReports`,
            HttpHeaderContentType.PDF,
            true,
            {
                reportLanguage: languageLocale,
                showSensitiveInformation: showSensitiveData,
                personsToReportOn: personIds
            }
        );
    }

    sendBulkCogReportEmail(
        personIdsToReportOn: string[],
        reportEmailConfig: ReportEmailConfiguration,
        showSensitiveData = true,
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ) {
        const payload = this.transformer.transformToServerCogReportConfig(
            personIdsToReportOn,
            reportEmailConfig,
            languageLocale,
            showSensitiveData
        );
        return this.api.post(
            `/api/v3/companies/${companyId}/persons/EmailCognitiveReports`,
            payload
        );
    }

    getCogReportPdf(
        personIds: string[],
        actionEndpoint: ReportRetrievalType = 'Print',
        emailDetails: BehavioralReportConfigEmailDetails = {},
        showSensitiveData = true,
        language: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<SafeResourceUrl | any> {
        const payload = {
            personsToReportOn: personIds,
            reportLanguage: language,
            showSensitiveInformation: showSensitiveData,
            ...emailDetails
        };
        if (actionEndpoint === 'Email') {
            // This is a case different from our typical pattern and
            //   the options were to do a refactor of the backend or to simply
            //   pass along this flag.
            (payload as any).emailReportToAssessmentTaker = true;
        }

        const postResourceType = HttpHeaderContentType.PDF;

        if (actionEndpoint !== 'Email') {
            return this.api.postResourceUrl(
                `/api/v3/companies/${companyId}/persons/${actionEndpoint}CognitiveReports`,
                payload,
                postResourceType
            );
        } else {
            return this.api.postBloblessResource(
                `/api/v3/companies/${companyId}/persons/${actionEndpoint}CognitiveReports`,
                payload
            );
        }
    }

    getBAReportInfo(
        personId: string,
        gender: string,
        reportConfig: BehavioralReportConfiguration,
        companyId: string = this.userProfile.currentCompany
    ): Promise<BehavioralReportData> {
        return this.getBAReportInfoData(
            personId,
            gender,
            companyId,
            reportConfig
        ).then(([response]) => {
            const baReportInfo = this.transformer.transformToBAReport(response);
            return baReportInfo;
        });
    }

    private getBAReportInfoData(
        personId: string,
        gender: string,
        companyId: string,
        reportConfig: BehavioralReportConfiguration
    ) {
        let urlToRequest = `/api/v4/companies/${companyId}/persons/behavioralReportInfo?PersonId=${personId}&LanguageLocale=${reportConfig.languageLocale}&IsManagementStyle=${reportConfig.isManagement}&IsSellingStyle=${reportConfig.isSelling}&IsInfluencingStyle=${reportConfig.isInfluencing}`;
        if (gender) {
            urlToRequest = urlToRequest.concat(`&Gender=${gender}`);
        }

        return this.api.get<BAReportServer>(urlToRequest);
    }

    getBAReportBulk(
        personsToReport: { personId: string; gender: string }[],
        reportConfig: BehavioralReportConfiguration,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PdfReference> {
        return this.api.getResourceUrlWithPdfReference(
            `/api/v4/companies/${companyId}/persons/printBehavioralReports`,
            HttpHeaderContentType.PDF,
            true,
            {
                languageLocale: reportConfig.languageLocale,
                isManagementStyle: reportConfig.isManagement,
                isSellingStyle: reportConfig.isSelling,
                isInfluencingStyle: reportConfig.isInfluencing,
                personsToReportOn: personsToReport
            }
        );
    }

    gettMatchingEmailRecordCount(emailAddress: string): Promise<number> {
        return this.api
            .get<number>(
                `/api/v3/companies/${
                    this.userProfile.currentCompany
                }/persons/matchingEmailsCount?emailAddress=${encodeURIComponent(
                    emailAddress
                )}`
            )
            .then(
                ([server]) => server
                // ${encodeURIComponent(name)}
            );
    }

    anonymizeByEmail(emailAddress: string) {
        return this.api.post<any, any>(
            `/api/v3/companies/${
                this.userProfile.currentCompany
            }/persons/anonymizebyEmail?emailAddress=${encodeURIComponent(
                emailAddress
            )}`,
            null
        );
    }

    /**
     * @deprecated createNewPerson uses core and classic APIs and should be replaced with v5 endpoints
     */
    async createNewPerson(person: PersonUpdate): Promise<Person> {
        const payload = this.transformer.transformPersonToPost(person);

        const [server] = await this.api.post<
            PersonServerPost,
            PersonServerResponse
        >(
            `/api/v4/companies/${this.userProfile.currentCompany}/persons`,
            payload
        );
        return this.transformer.transformPersonFromPost(server, person);
    }
}
